import { useState } from 'react';
import apiService from '../services/api-service';
import { validateResetPasswordForm } from '../validators';
import { AUTH_MESSAGES, CONSOLE_MESSAGES } from '../constants';
import { MODAL_CLASSES } from '../styles';

function ResetPasswordModal({ isOpen, onClose, token }) {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(''); // 'success' or 'error'
  const [errors, setErrors] = useState({});

  // Handle modal close
  const handleClose = () => {
    setFormData({ newPassword: '', confirmPassword: '' });
    setMessage('');
    setStatus('');
    setErrors({});
    onClose();
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const { isValid, errors: validationErrors } = validateResetPasswordForm(formData);
    setErrors(validationErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const data = await apiService.resetPassword(token, formData.newPassword);
      setStatus('success');
      setMessage(data.message || AUTH_MESSAGES.PASSWORD_CHANGED);
    } catch (error) {
      console.error(CONSOLE_MESSAGES.RESET_PASSWORD_ERROR, error);
      setStatus('error');
      setMessage(error.message || AUTH_MESSAGES.PASSWORD_CHANGE_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={MODAL_CLASSES.OVERLAY}
      onClick={handleOverlayClick}
    >
      <div className="relative w-full max-w-md bg-dark-800 rounded-xl sm:rounded-2xl shadow-2xl border border-dark-700 overflow-hidden max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          aria-label="Uždaryti"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="p-4 sm:p-6 pb-3 sm:pb-4">
          <div className="text-center">
            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 ${
              status === 'success' 
                ? 'bg-gradient-to-br from-green-500 to-green-700'
                : 'bg-gradient-to-br from-blue-500 to-blue-700'
            }`}>
              {status === 'success' ? (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m0 0a2 2 0 01-2 2m2-2h3m-3 0H9m3 0v6m-1-6a2 2 0 11-4 0m1 6V9a2 2 0 00-2-2m0 0V5a2 2 0 012-2h.01M9 7V5a2 2 0 012-2h.01M13 16h-1v-4h-1m1-4h.01" />
                </svg>
              )}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1.5 sm:mb-2">
              {status === 'success' ? 'Slaptažodis pakeistas!' : 'Nustatyti naują slaptažodį'}
            </h2>
            <p className="text-gray-400 text-xs sm:text-sm">
              {status === 'success' 
                ? 'Jūsų slaptažodis sėkmingai pakeistas'
                : 'Įveskite naują saugų slaptažodį savo paskyrai'
              }
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6">
          {status === 'success' ? (
            // Success state
            <div className="space-y-3 sm:space-y-4">
              <div className="p-3 sm:p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-400 font-medium text-xs sm:text-sm">
                    Sėkmingai atnaujinta!
                  </span>
                </div>
                <p className="text-green-300 text-xs sm:text-sm">
                  {message}
                </p>
              </div>

              <div className="text-center space-y-2 sm:space-y-3">
                <p className="text-gray-300 text-sm">
                  Dabar galite prisijungti su nauju slaptažodžiu.
                </p>
                
                <ul className="text-gray-400 text-xs sm:text-sm space-y-1.5 sm:space-y-2">
                  <li className="flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Saugus slaptažodis nustatytas
                  </li>
                  <li className="flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Paskyra saugi ir prieinama
                  </li>
                </ul>
              </div>

              <button
                onClick={handleClose}
                className="btn-primary w-full justify-center h-10 sm:h-11 text-sm sm:text-base"
              >
                Prisijungti dabar
              </button>
            </div>
          ) : (
            // Form state
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* Error message */}
              {status === 'error' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs sm:text-sm">
                  {message}
                </div>
              )}

              {/* New password field */}
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="newPassword" className="block text-xs sm:text-sm font-medium text-gray-300">
                  Naujas slaptažodis
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className={`input-primary w-full pr-10 sm:pr-12 text-sm sm:text-base ${errors.newPassword ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="Įveskite naują slaptažodį"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSubmitting}
                    className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M9.878 9.878l4.242 4.242M14.12 14.12l1.415 1.415M14.12 14.12l4.242 4.242M14.12 14.12L19.85 19.85" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-red-400 text-xs">{errors.newPassword}</p>
                )}
                <p className="text-gray-400 text-[11px] sm:text-xs">
                  Slaptažodis turi turėti bent 8 simbolius, didžiąją raidę, mažąją raidę ir skaičių
                </p>
              </div>

              {/* Confirm password field */}
              <div className="space-y-1.5 sm:space-y-2">
                <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-300">
                  Patvirtinti slaptažodį
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className={`input-primary w-full pr-10 sm:pr-12 text-sm sm:text-base ${errors.confirmPassword ? 'border-red-500 focus:border-red-500' : ''}`}
                    placeholder="Pakartokite naują slaptažodį"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isSubmitting}
                    className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M9.878 9.878l4.242 4.242M14.12 14.12l1.415 1.415M14.12 14.12l4.242 4.242M14.12 14.12L19.85 19.85" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-400 text-xs">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center relative disabled:opacity-50 disabled:cursor-not-allowed h-10 sm:h-11 text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Keičiama...
                  </>
                ) : (
                  'Pakeisti slaptažodį'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordModal; 